import type React from 'react';

import { Button } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { PostPreviewSizes, RetryState } from 'src/app/pages/starterDashboardV2/shared/components';
import { PostPreview } from 'src/app/pages/starterDashboardV2/shared/components';
import type { EnrichedReadyToSharePlatformPostV2 } from 'src/app/shared/api/readyToShare';

import { EMPTY_SHARING_RESOURCE } from '../constants';
import { getCTALabelForPostType } from '../utils';

type Props = {
	postTypeName: EnrichedReadyToSharePlatformPostV2['__typename'];
	petition: StarterDashboardV2PageData['petition'];
	previewSize: PostPreviewSizes;
	retryState: RetryState;
	onRetry: () => void;
};

export const PostPreviewCardRetry: React.FC<Props> = (props) => {
	const { postTypeName, petition, previewSize, retryState, onRetry } = props;
	const { translate } = useI18n();

	const isRetrying = retryState === 'retrying';
	const buttonText = isRetrying
		? translate('corgi.petition.starter_dashboard_v2.page_body.post_preview_card_retry.retrying')
		: getCTALabelForPostType(translate, postTypeName);

	return (
		<section
			key={`${postTypeName}-retry`}
			className="flex gap-3"
			data-testid={`${postTypeName}-post-card-retry`}
			aria-label={`Retry ${postTypeName}`}
		>
			<div className="flex flex-col gap-3 gap-y-6">
				<PostPreview
					variant={postTypeName}
					sharingResource={EMPTY_SHARING_RESOURCE}
					size={previewSize}
					loading
					showPlatformIcon
					data-testid={`${postTypeName}-post-preview-retry`}
					petition={petition}
					disabled={isRetrying}
					retryState={isRetrying ? undefined : 'failed'}
					onRetry={onRetry}
				/>
				<Button
					variant="secondary"
					size="x-small"
					disabled
					data-qa={`${postTypeName}-retry-button`}
					data-testid={`${postTypeName}-retry-button`}
					aria-label={buttonText}
					className="w-full min-w-36 no-underline md:min-w-0"
				>
					{buttonText}
				</Button>
			</div>
		</section>
	);
};
